import { getSiteCountries } from '@_api/siteCountries';
import { getClassFunction } from '@helpers/functions';
import { SiteCountryModelApiResponse } from '@_types/_api-response';
import { DropdownOption } from '@_types/atoms';
import { currentViewport, DEVICES } from '@helpers/breakpoint';

const getClass = getClassFunction('kf-o-country-selector-prompt');

const globalValue = 'global';
const COUNTRY_SELECTOR_PROMPT = 'COUNTRY_SELECTOR_PROMPT';

class CountrySelectorPrompt {
  element: HTMLDivElement;
  dropdown: HTMLDivElement | null;
  countries: SiteCountryModelApiResponse[] = [];

  constructor(element: HTMLDivElement) {
    this.element = element;
    this.dropdown = this.element.querySelector<HTMLDivElement>('.kf-a-dropdown');
  }

  init() {
    this.addEventToDropdown();
    this.addEventToCloseIcon();
    this.checkPromptShouldOpen();
    this.onDropdownInit();
  }

  async handleGetSiteCountries() {
    const response = await getSiteCountries({});

    if (!response?.success) return;

    return response.data;
  }

  checkPromptShouldOpen() {
    const isClosed = sessionStorage.getItem(COUNTRY_SELECTOR_PROMPT);

    if (isClosed) {
      this.hidePrompt();
    } else {
      this.showPrompt();
    }
  }

  addEventToDropdown() {
    this.dropdown?.addEventListener('dropdown-change', this.onDropdownChange.bind(this));
  }

  addEventToCloseIcon() {
    const closeIcons = this.element.querySelectorAll<HTMLElement>('.kf-o-country-selector-prompt__close-icon');

    closeIcons.forEach((icon) => {
      icon.addEventListener('click', this.onClose.bind(this));
      icon.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          this.onClose();
        }
      });
    });
  }

  onClose() {
    this.hidePrompt();
    sessionStorage.setItem(COUNTRY_SELECTOR_PROMPT, 'true');
  }

  showPrompt() {
    this.element.dataset.hide = 'false';

    this.triggerHeaderChangeHeightEvent();
    this.changePromptState('show');
  }

  triggerHeaderChangeHeightEvent() {
    if (currentViewport() === DEVICES.DESKTOP) return;

    const header = this.element.closest<HTMLElement>('.kf-o-header');

    header?.dispatchEvent(new CustomEvent('header-change-height-event'));
  }

  hidePrompt() {
    this.element.dataset.hide = 'true';

    this.triggerHeaderChangeHeightEvent();
    this.changePromptState('hide');
  }

  changePromptState(state: 'show' | 'hide') {
    if (state === 'show') {
      document.body.classList.add('prompt-open');
    } else {
      document.body.classList.remove('prompt-open');
    }
  }

  async onDropdownInit() {
    if (this.countries.length) return;

    const data = await this.handleGetSiteCountries();

    if (data?.countries?.length) {
      this.countries = data.countries;
      const globalLabel = this.element.dataset.globalLabel;
      const countryLabel = this.element.dataset.countryLabel;

      const placeholderFlag = this.element.querySelector('.kf-a-dropdown__label-text .fflag');
      const placeholderGlobalIcon = this.element.querySelector('.kf-a-dropdown__label-text .kf-o-country-selector-prompt__global-icon');
      let selectedCountryCode = data.userCountryCode;
      let hasPreselectedCountry = false;

      if (placeholderFlag) {
        const flagClass = Array.from(placeholderFlag.classList).find((cls) => cls.startsWith('fflag-'));

        if (flagClass) {
          selectedCountryCode = flagClass.replace('fflag-', '').toLowerCase();
          hasPreselectedCountry = true;
        }
      } else if (placeholderGlobalIcon) {
        selectedCountryCode = globalValue;
        hasPreselectedCountry = true;
      }

      const userCountryExist = data.countries.find((country) => country.countryCode === selectedCountryCode);
      const global: SiteCountryModelApiResponse[] = [];
      const otherCountries: SiteCountryModelApiResponse[] = [];

      data.countries.forEach((country) => {
        if (country?.countryCode === globalValue) {
          global.push(country);

          return;
        }

        otherCountries.push(country);
      });

      const globalOptions = this.transformDataToDropdownOptions({
        countries: global,
        selected: hasPreselectedCountry && !userCountryExist,
        userCountryCode: selectedCountryCode,
      });
      const otherCountryOptions = this.transformDataToDropdownOptions({
        countries: otherCountries,
        userCountryCode: hasPreselectedCountry ? selectedCountryCode : data.userCountryCode,
      });

      const dropdownOptions = this.dropdown?.querySelector<HTMLDivElement>('.kf-a-dropdown__options');

      if (!dropdownOptions) return;

      dropdownOptions.appendChild(this.createGroupOptionFragment(globalOptions, globalLabel));
      dropdownOptions.appendChild(this.createGroupOptionFragment(otherCountryOptions, countryLabel));

      this.dropdown?.dispatchEvent(new CustomEvent('init-options'));
    }
  }

  onDropdownChange(event: CustomEvent) {
    const value = event.detail.value as string;
    const text = event.detail.text as string;
    const link = event.detail.dataset.link;
    const labelText = this.dropdown?.querySelector<HTMLDivElement>('.kf-a-dropdown__label-text');

    if (!value || !text || !labelText) return;

    if (link) {
      const form = this.element.querySelector<HTMLFormElement>('.kf-o-country-selector-prompt__form');

      if (!form) return;

      form.action = link;
    }

    if (value !== globalValue) {
      this.setCountryLabelText(labelText, value, text);
    } else {
      this.setGlobalLabelText(labelText, text);
    }
  }

  private setCountryLabelText(labelText: HTMLElement, value: string, text: string): void {
    labelText.innerHTML = '';
    labelText.appendChild(this.createFlagIcon(value, text));
    labelText.appendChild(this.createCountryNameText(text));
  }

  private setGlobalLabelText(labelText: HTMLElement, text: string): void {
    labelText.innerHTML = '';
    labelText.appendChild(this.createGlobalIcon());
    labelText.appendChild(this.createCountryNameText(text));
  }

  private createFlagIcon(value: string, text: string): HTMLSpanElement {
    const span = document.createElement('span');

    span.className = `fflag fflag-${value.toUpperCase()} kf-o-country-selector-prompt__current-flag-icon`;
    span.title = text.trim();

    return span;
  }

  private createGlobalIcon(): HTMLSpanElement {
    const span = document.createElement('span');

    span.className = 'kf-o-country-selector-prompt__global-icon';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('class', 'kf-a-icon');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');

    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/assets/images/icons.svg#kf-globe');
    svg.appendChild(use);
    span.appendChild(svg);

    return span;
  }

  private createCountryNameText(text: string): HTMLSpanElement {
    const span = document.createElement('span');

    span.className = 'kf-o-country-selector-prompt__current-country';
    span.textContent = text.trim();

    return span;
  }

  private transformDataToDropdownOptions({
    countries,
    selected,
    userCountryCode,
  }: {
    countries: SiteCountryModelApiResponse[];
    selected?: boolean;
    userCountryCode?: string;
  }): DropdownOption[] {
    return countries.map((country) => ({
      text: country.countryName,
      value: country.countryCode,
      selected: selected || userCountryCode === country.countryCode,
      others: {
        link: country.targetLink,
      },
    }));
  }

  private createGroupOptionFragment(options: DropdownOption[], label?: string): DocumentFragment {
    const fragment = document.createDocumentFragment();
    const groupDiv = document.createElement('div');

    groupDiv.className = 'kf-a-dropdown-group__item-group';
    groupDiv.setAttribute('role', 'group');
    groupDiv.setAttribute('aria-label', label || '');

    const labelP = document.createElement('p');

    labelP.className = 'kf-a-dropdown-group__item-group-label';
    labelP.textContent = label || '';
    groupDiv.appendChild(labelP);

    groupDiv.appendChild(this.createOptionsFragment(options));
    fragment.appendChild(groupDiv);

    return fragment;
  }

  private createOptionsFragment(options: DropdownOption[]): DocumentFragment {
    const fragment = document.createDocumentFragment();

    options.forEach((option) => fragment.appendChild(this.createOptionElement(option)));

    return fragment;
  }

  private createOptionElement(option: DropdownOption): HTMLDivElement {
    const dropdownName = this.dropdown?.dataset?.name;
    const optionDiv = document.createElement('div');

    optionDiv.className = 'kf-a-dropdown__option kf-a-dropdown-group__child-option';
    optionDiv.setAttribute('role', 'option');
    optionDiv.setAttribute('aria-selected', String(option.selected));
    optionDiv.setAttribute('aria-label', String(option.text));
    optionDiv.setAttribute('tabindex', '0');

    optionDiv.appendChild(this.createOptionInput(option, dropdownName));
    optionDiv.appendChild(this.createOptionLabel(option, dropdownName));

    return optionDiv;
  }

  private createOptionInput(option: DropdownOption, dropdownName?: string): HTMLInputElement {
    const input = document.createElement('input');

    input.id = `${dropdownName}${option?.value}`;
    input.name = dropdownName || '';
    input.value = option?.value || '';
    input.type = 'radio';
    if (option.selected) input.checked = true;
    if (option?.others?.link) input.setAttribute('data-link', String(option.others.link));

    return input;
  }

  private createOptionLabel(option: DropdownOption, dropdownName?: string): HTMLLabelElement {
    const label = document.createElement('label');

    label.htmlFor = `${dropdownName}${option?.value}`;

    const checkmark = document.createElement('span');

    checkmark.className = 'checkmark';
    label.appendChild(checkmark);

    label.appendChild(this.createOptionIcon(option));

    const textSpan = document.createElement('span');

    textSpan.className = `kf-o-country-selector-prompt__option-text ${option.value !== globalValue ? 'kf-o-country-selector-prompt__country-option-text' : ''}`;
    textSpan.textContent = String(option.text);
    label.appendChild(textSpan);

    return label;
  }

  private createOptionIcon(option: DropdownOption): HTMLElement {
    if (option.value !== globalValue) {
      const iconDiv = document.createElement('div');

      iconDiv.className = `fflag fflag-${option.value?.toUpperCase()} kf-o-country-selector-prompt__flag-icon`;
      iconDiv.title = String(option.text);

      return iconDiv;
    }

    const globalIconSpan = document.createElement('span');

    globalIconSpan.className = 'kf-o-country-selector-prompt__global-icon';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

    svg.setAttribute('class', 'kf-a-icon');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');

    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/assets/images/icons.svg#kf-globe');
    svg.appendChild(use);
    globalIconSpan.appendChild(svg);

    return globalIconSpan;
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const countrySelectorPrompt = document.querySelector<HTMLDivElement>(getClass());

  if (countrySelectorPrompt) {
    new CountrySelectorPrompt(countrySelectorPrompt).init();
  }
});

export {};
